import { GoogleChrome } from '@kula-ai/icons-core';
import { GoogleWorkspaceLogo } from './GoogleWorkspaceLogo';
import { Microsoft } from '@/assets/icons/mailbox';
import { EXTENSION_ID } from '@/constants/app-constants';
import { ConnectWithCalForm } from '@/components/calendar/ConnectWithCalForm';
import { ConnectWithGmailForm } from '@/components/shared/ConnectWithGmailForm';
import { ConnectWithMsForm } from '@/components/mailbox/ConnectWithMsForm';
import { useDispatch, useSelector } from 'react-redux';
import { selectMeForUpdate } from '@/store/me';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import dayjs from 'dayjs';
import { useIsMobile } from '@/hooks/use-IsMobile';

const getRemainingDays = (time) => {
  const difference = dayjs(time).diff(Date.now(), 'day');
  if (difference >= 2) return ` ${difference} days`;
  else if (difference === 1) return ` 1 day`;
  else return `Today`;
};

export const useNudgesConfig = () => {
  const isMobile = useIsMobile(); // 👈 detect mobile
  const dispatch = useDispatch();
  const { user } = useSelector(selectMeForUpdate);
  const currUser = useSelector(selectCurrUser);

  if (isMobile) {
    return {};
  }
  const isAdminAbove = ['owner', 'admin'].includes(currUser?.role);

  const daysDifference = dayjs(currUser?.account?.expires_at).diff(
    // eslint-disable-next-line react-hooks/purity -- intentional render-time relative-day calc
    Date.now(),
    'day'
  );
  const days = daysDifference >= 1 ? 'in' : '';
  const remainingDays = getRemainingDays(currUser?.account?.expires_at);

  const nudges = {
    trailUser: {
      id: 'trailUser',
      bgColor: 'ats-negative',
      inlineIcon: '',
      variant: 'primary',
      title: `Your free trial ends ${days} ${remainingDays}.`,
      subTitle: `Don't lose access, ${
        isAdminAbove
          ? `Upgrade your account now.`
          : 'contact your Kula workspace admin to upgrade and continue hiring amazing team members.'
      }`,
      ...(isAdminAbove && {
        cta: {
          label: 'Contact us',
          bgColor: 'white',
          color: 'ats-negative',
          linkProps: {
            href: `mailto:support@kula.ai`,
            target: '_blank',
          },
          onClick: () => {},
        },
      }),
    },
    linkedinSync: {
      id: 'linkedinSync',
      bgColor: 'ats-negative',
      title: 'Your LinkedIn settings are out of sync.',
      subTitle:
        'Please make sure the settings are set correctly. Until fixed, all Flows with LinkedIn steps will be on hold',
      cta: {
        label: 'Set it up',
        color: 'white',
        bgColor: 'ats-primary',
        linkProps: {
          href: 'https://www.linkedin.com/mypreferences/d/manage-email-addresses',
          target: '_blank',
        },
        onClick: () => {},
      },
    },
    kulaExtension: {
      id: 'kulaExtension',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      variant: 'secondary',
      title: 'Install Kula Everywhere',
      subTitle:
        'Add your candidates to flow seamlessly via Kula Everywhere. Our extension currently supports multiple websites, including LinkedIn, Github, and more.',
      cta: {
        icon: GoogleChrome,
        label: 'Install extension',
        bgColor: 'ats-primary',
        color: 'white',
        linkProps: {
          href: `https://chrome.google.com/webstore/detail/kula-everywhere-developme/${EXTENSION_ID}`,
          target: '_blank',
        },
        onClick: () => {},
      },
    },
    gmailCalReconnect: {
      id: 'gmailCalReconnect',
      bgColor: 'ats-negative',
      inlineIcon: '',
      title: 'Gmail disconnected from Kula',
      subTitle:
        'All your Flows are paused. Connect your Google account to Kula to resume sending messages.',
      cta: {
        icon: GoogleWorkspaceLogo,
        Wrapper: ConnectWithCalForm,
        wrapperProps: {
          variant: 'gcal',
          dispatch,
        },
        label: 'Reconnect your Google account',
        onClick: () => {},
      },
    },
    outlookCalReconnect: {
      id: 'outlookCalReconnect',
      bgColor: 'ats-negative',
      title: 'Outlook disconnected from Kula.',
      subTitle:
        'All your Flows are paused. Connect your Microsoft account to Kula to resume sending messages.',
      cta: {
        icon: Microsoft,
        Wrapper: ConnectWithCalForm,
        wrapperProps: {
          variant: 'mscal',
          dispatch,
        },
        label: 'Reconnect your Microsoft account',
        onClick: () => {},
      },
    },
    googleReconnect: {
      id: 'googleReconnect',
      bgColor: 'ats-negative',
      title: 'Google disconnected from Kula.',
      subTitle:
        'Integrate your Calendar with Kula for seamless service resumption.',
      cta: {
        Wrapper: ConnectWithGmailForm,
        wrapperProps: {
          user: user,
          dispatch,
        },
        icon: GoogleWorkspaceLogo,
        label: 'Reconnect your Google calendar',
        onClick: () => {},
      },
    },
    microsoftReconnect: {
      id: 'microsoftReconnect',
      bgColor: 'ats-negative',
      title: 'Microsoft disconnected from Kula.',
      subTitle:
        'Integrate your Calendar with Kula for seamless service resumption.',
      cta: {
        icon: Microsoft,
        Wrapper: ConnectWithMsForm,
        wrapperProps: {
          user: currUser,
          dispatch,
        },
        label: 'Reconnect your Outlook Calendar',
        onClick: () => {},
      },
    },
    isAccountLoggedOut: {
      id: 'isAccountLoggedOut',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: 'Oops! Your LinkedIn account is not logged in.',
      subTitle: `Please log in to continue.`,
      variant: 'secondary',
      cta: {
        label: 'Login',
        color: 'white',
        bgColor: '#FF7800',
        linkProps: {
          href: 'https://www.linkedin.com/login',
          target: '_blank',
        },
        onClick: () => {},
      },
    },
    isRecruiterLoggedOut: {
      id: 'isRecruiterLoggedOut',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: 'It seems that your LinkedIn Recruiter account is logged out. ',
      subTitle: `Please log in now.`,
      variant: 'secondary',
      cta: {
        label: 'Login',
        color: 'white',
        bgColor: '#FF7800',
        linkProps: {
          href: 'https://www.linkedin.com/uas/login-cap',
          target: '_blank',
        },
        onClick: () => {},
      },
    },
    isInMailLimitOver: {
      id: 'isInMailLimitOver',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: 'Wow! An overachiever.',
      subTitle: `You have exhausted the LinkedIn InMail limit.
			We'll resume your tasks tomorrow.`,
      variant: 'secondary',
    },
    isInternalInMailLimitOver: {
      id: 'isInternalInMailLimitOver',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: 'Wow! An overachiever.',
      subTitle: `You have exhausted the daily internal InMail limit. You can adjust your limits`,
      variant: 'secondary',
      cta: {
        label: 'Update here',
        color: 'white',
        bgColor: '#FF7800',
        linkProps: {
          href: '/settings/messaging-signatures',
        },
        onClick: () => {},
      },
    },
    isConnectionRequestOver: {
      id: 'isConnectionRequestOver',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: "You're a busy bee!",
      subTitle: `You have exhausted the LinkedIn connection request limit. We'll resume your tasks next week.`,
      variant: 'secondary',
    },
    isInternalConWeeklyReqOver: {
      id: 'isInternalConWeeklyReqOver',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: "You're a social butterfly!",
      subTitle: `You've exhausted the the weekly internal connection request limit. We'll resume your tasks next week. You can adjust your limits`,
      variant: 'secondary',
      cta: {
        label: 'Update here',
        color: 'white',
        bgColor: '#FF7800',
        linkProps: {
          href: '/settings/messaging-signatures',
        },
        onClick: () => {},
      },
    },
    isInMailCreditsOver: {
      id: 'isInMailCreditsOver',
      bgColor: 'rgb(255 242 204 / var(--kula-dark-pastel-alpha, 1))',
      title: 'Uh oh! You seem to be out of InMail credits. ',
      subTitle: `Refresh them on LinkedIn to continue sending InMails through Kula.`,
      variant: 'secondary',
    },
  };
  return nudges;
};
